import { forwardRef, useImperativeHandle, useState } from "react";
import { Fragment } from "react/jsx-runtime";
import { Box, CircularProgress, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { BaseFormModalPropsType, BaseFormModalRefType } from "../../../types/app.type";
import { BaseFormModalWrapper } from "./styled";
import CloseIcon from '@mui/icons-material/Close';
import { BaseFieldSet } from "../../formfields/fieldset/styled";
import { BaseInput } from "../../formfields/input/styled";
import { BaseButton } from "../../button/styled";
import { BaseLabel } from "../../formfields/label/styled";

export const BaseFormModal = forwardRef<BaseFormModalRefType, BaseFormModalPropsType>(({ open, className, handleClose, title, location, secondaryCallToAction, handleSecondaryCTAClick, error, isLoading, callToAction, handleSubmit, resendOTP }, ref) => {
    const [OTP, setOTP] = useState("");
    const [roleDetails, setRoleDetails] = useState({
        title: "",
        description: "",
    });

    useImperativeHandle(ref, () => ({
        getRoleDetails: () => roleDetails,
        clearRoleDetails: () => setRoleDetails({ title: "", description: "" }),
        setRoleDetails: (selectedRole) => setRoleDetails(selectedRole ?? { title: "", description: "" }),
        getOTP: () => OTP,
    }));

    const handleOTPUpdate = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = e.target;
        setOTP(value);
    };

    const handleRoleUpdate = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setRoleDetails((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    return (
        <BaseFormModalWrapper
            open={open}
            className={className}
            onClose={handleClose}
            location={location as string}
        >
            {(secondaryCallToAction) && (
                <Box
                    component={"div"}
                    className="icon-button-box"
                >
                    <IconButton
                        onClick={(event) => handleClose && handleClose(event, "backdropClick")}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            )}
            <Stack
                direction={"row"}
                className="modal-heading"
            >
                <DialogTitle>
                    {title}
                </DialogTitle>
                {(secondaryCallToAction) ? (
                    <BaseButton
                        variant="contained"
                        fontSize={14}
                        bgcolor="#791377"
                        padding="calc(var(--basic-padding)/2)"
                        onClick={handleSecondaryCTAClick}
                    >
                        <Typography
                            variant={"button"}
                            fontFamily={"inherit"}
                            fontWeight={"inherit"}
                            fontSize={"inherit"}
                            lineHeight={"inherit"}
                            color={"inherit"}
                            textTransform={"inherit"}
                        >
                            {secondaryCallToAction}
                        </Typography>
                    </BaseButton>
                ) : (
                    (location === "userrole") &&
                    <IconButton
                        onClick={(event) => handleClose && handleClose(event, "backdropClick")}
                    >
                        <CloseIcon />
                    </IconButton>
                )}
            </Stack>
            <DialogContent
                className="modal-content"
            >
                <form
                    onSubmit={handleSubmit}
                >
                    {(location === "userrole") && (
                        <Fragment>
                            <BaseFieldSet>
                                <BaseLabel>Name</BaseLabel>
                                <BaseInput
                                    required
                                    name="title"
                                    value={roleDetails.title}
                                    placeholder="Input role name"
                                    onChange={(e) => handleRoleUpdate(e)}
                                />
                            </BaseFieldSet>
                            <BaseFieldSet>
                                <BaseLabel>Description</BaseLabel>
                                <BaseInput
                                    required
                                    multiline
                                    minRows={4}
                                    name="description"
                                    value={roleDetails.description}
                                    placeholder="Description of role"
                                    onChange={(e) => handleRoleUpdate(e)}
                                />
                            </BaseFieldSet>
                        </Fragment>
                    )}
                    {(location === "password-recovery") && (
                        <Fragment>
                            <BaseFieldSet>
                                <BaseLabel>OTP</BaseLabel>
                                <BaseInput
                                    required
                                    name="OTP"
                                    value={OTP}
                                    placeholder="Enter OTP"
                                    onChange={(e) => handleOTPUpdate(e)}
                                />
                            </BaseFieldSet>
                            <Typography
                                component={"p"}
                                fontFamily={"Inter"}
                                fontWeight={"600"}
                                fontSize={16}
                                lineHeight={"normal"}
                                color={"#791377"}
                                whiteSpace={"normal"}
                                textAlign={"right"}
                                marginBlockStart={"calc(var(--basic-margin) * 0.5)"}
                                sx={{ cursor: "pointer" }}
                                onClick={resendOTP}
                            >
                                Resend OTP
                            </Typography>
                        </Fragment>
                    )}
                    {error && <Typography
                        fontFamily={"Inter"}
                        fontWeight={"600"}
                        fontSize={16}
                        lineHeight={"normal"}
                        color={"#FF0000"}
                        whiteSpace={"normal"}
                        textAlign={"center"}
                        marginBlockStart={"calc(var(--basic-margin) * 0.5)"}
                    >
                        {error}
                    </Typography>}
                    <DialogActions
                        className="call-to-action"
                    >
                        <BaseButton
                            type="submit"
                            variant="contained"
                            bgcolor="#791377"
                            sx={{
                                width: "100%"
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    {callToAction ?? "Continue"}
                                </Typography>
                            )}
                        </BaseButton>
                    </DialogActions>
                </form>
            </DialogContent>
        </BaseFormModalWrapper >
    )
})
